import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import { IStpEntityResponse, IStpSupplierResponse } from "../interfaces/stp";
import {
  IFetchStpRulesParams,
  IStpRulesResponse,
  IUpdateStpRulesParams,
} from "../interfaces/stpRules";
import { OrganizationSettings } from "../../user/interfaces/responseModels";

export const organizationApi = createApi({
  reducerPath: "organizationApi",
  baseQuery: customBaseQuery(),
  endpoints: (build) => ({
    fetchStpSuppliers: build.query<
      Array<IStpSupplierResponse>,
      { organizationId: string; inputIds: Array<string> }
    >({
      query: ({ organizationId, inputIds }) => ({
        url: `/api/v1/stp/suppliers?organization-id=${organizationId}&input-ids=${inputIds}`,
        method: "GET",
      }),
    }),
    insertStpSupplier: build.query<IStpSupplierResponse, IStpSupplierResponse>({
      query: (stpSupplier) => ({
        url: `/api/v1/stp/suppliers`,
        body: {
          ...stpSupplier,
        },
        method: "POST",
      }),
    }),
    removeStpSuppliers: build.query<void, { ids: Array<string> }>({
      query: (data) => ({
        url: `/api/v1/stp/suppliers`,
        body: {
          ...data,
        },
        method: "DELETE",
      }),
    }),
    fetchStpEntities: build.query<Array<IStpEntityResponse>, string>({
      query: (docTypeId) => ({
        url: `/api/v1/stp/entities?document-type-id=${docTypeId}`,
        method: "GET",
      }),
    }),
    addOrUpdateStpEntity: build.query<IStpEntityResponse, IStpEntityResponse>({
      query: (stpEntity) => ({
        url: `/api/v1/stp/entities`,
        body: {
          ...stpEntity,
        },
        method: "POST",
      }),
    }),
    removeStpEntities: build.query<void, { ids: Array<string> }>({
      query: (data) => ({
        url: `/api/v1/stp/entities`,
        body: {
          ...data,
        },
        method: "DELETE",
      }),
    }),
    fetchStpRules: build.query<IStpRulesResponse, IFetchStpRulesParams>({
      query: ({ documentTypeId, stpType }) => ({
        url: `/api/v1/stp/rules?document-type-id=${documentTypeId}&stp-type=${stpType}`,
        method: "GET",
      }),
    }),
    updateStpRules: build.query<IStpRulesResponse, IUpdateStpRulesParams>({
      query: ({ documentTypeId, stpType, enabled, rules }) => ({
        url: `/api/v1/stp/rules`,
        body: { documentTypeId, stpType, enabled, rules },
        method: "POST",
      }),
    }),
    fetchOrganizationSettings: build.query<OrganizationSettings, string>({
      query: (organizationId) => ({
        url: `/api/v1/organization/settings/${organizationId}`,
        method: "GET",
      }),
    }),
    updateOrganisationSettings: build.query<
      void,
      { organizationId: string; settings: OrganizationSettings }
    >({
      query: ({ organizationId, settings }) => ({
        url: `/api/v1/organization/settings/${organizationId}`,
        body: {
          ...settings,
        },
        method: "PUT",
      }),
    }),
  }),
});

export const {
  useLazyFetchStpRulesQuery,
  useLazyUpdateStpRulesQuery,
  useLazyFetchStpSuppliersQuery,
  useLazyInsertStpSupplierQuery,
  useLazyRemoveStpSuppliersQuery,
  useLazyFetchStpEntitiesQuery,
  useLazyAddOrUpdateStpEntityQuery,
  useLazyRemoveStpEntitiesQuery,
  useLazyFetchOrganizationSettingsQuery,
  useLazyUpdateOrganisationSettingsQuery,
} = organizationApi;
